import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { GameState, GamePhase } from '../types';

// === Types ===

/** Request for one half of the animation */
export interface HalfRequest {
    direction: 'forward' | 'reverse';
    requestKey: number;
}

/** Two-channel request: top and bottom halves are independent */
export interface MugPlayRequest {
    top?: HalfRequest;
    bottom?: HalfRequest;
}

/** Queued animation item */
interface QueuedAnimation {
    direction: 'forward' | 'reverse';
}

export interface MugAnimationController {
    cheersPlayKey: number;
    mugRequest: MugPlayRequest;
    onCheersDone: () => void;
    onMugDone: () => void;
    /** Called when a half finishes its animation - triggers next in queue */
    onHalfComplete: (half: 'top' | 'bottom') => void;
    /** True when Pass button should be temporarily locked (1s after animation starts) */
    passButtonLocked: boolean;
}

// === Night Event Detection Helpers ===

/**
 * Night context includes Combat phase AND all night modal phases.
 * This prevents false NightStart triggers when returning from modal choices.
 */
const isNightContext = (phase: GamePhase): boolean =>
    phase === GamePhase.Combat ||
    phase === GamePhase.TowerPurchase ||
    phase === GamePhase.AWAITING_HIBERNATION_CHOICE ||
    phase === GamePhase.AWAITING_CHROMABOW_CHOICE ||
    phase === GamePhase.AWAITING_BURGLARY_CHOICE ||
    phase === GamePhase.AWAITING_PURIFICATION_TARGET ||
    phase === GamePhase.LifeChargerPending ||
    phase === GamePhase.BROKEN_SECRET_REVEAL;

const isCombatPhase = (phase: GamePhase): boolean =>
    phase === GamePhase.Combat;

const isPreparationPhase = (phase: GamePhase): boolean =>
    phase === GamePhase.Preparation;

/** Map player ID to which half they control on screen */
const halfForPlayer = (playerId: number, localPlayerId: number): 'top' | 'bottom' =>
    playerId === localPlayerId ? 'bottom' : 'top';

// === Hook ===

/**
 * Controller hook for managing Cheers and TakePutMug animations.
 * 
 * Uses a queue-based system to prevent race conditions when game events
 * arrive faster than animations can complete.
 * 
 * Event detection:
 * - NightStart: transition to Combat phase (from non-night context)
 * - DayStart: transition to Preparation phase (from non-Preparation)
 * - PassTurnEvent: turnPlayerId changed AND consecutivePasses increased by 1
 * - NightEnd: transition to RoundEnd phase
 * 
 * Animation rules:
 * - Cheers plays on both NightStart and DayStart
 * - After Cheers on NightStart: TakePut reverse for first player's side
 * - On Pass: passer gets reverse (once per night), receiver gets forward (if pending)
 * - Both halves are independent and can animate simultaneously
 * - Animations are queued and processed sequentially per half
 */
export const useMugAnimationController = (
    gameState: GameState | null,
    localPlayerId: number,
    cheersPlaying: boolean
): MugAnimationController => {
    // === Play keys for triggering animations ===
    const [cheersPlayKey, setCheersPlayKey] = useState(0);
    const [mugRequest, setMugRequest] = useState<MugPlayRequest>({});

    // Separate request key counters for each half
    const topRequestKeyRef = useRef(0);
    const bottomRequestKeyRef = useRef(0);

    // === Animation queues for each half ===
    const topQueueRef = useRef<QueuedAnimation[]>([]);
    const bottomQueueRef = useRef<QueuedAnimation[]>([]);

    // === Track if animation is currently playing on each half ===
    const topPlayingRef = useRef(false);
    const bottomPlayingRef = useRef(false);

    // === Pass button lock state (1s after animation starts) ===
    const [passButtonLocked, setPassButtonLocked] = useState(false);
    const passLockTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // === Per-night state for BOTH players (reset on NightStart) ===
    const nightKeyRef = useRef<number | null>(null);
    const nightFirstPlayerIdRef = useRef<number | null>(null);

    // Track reverse/forward state per player ID (1 or 2)
    const reversePlayedByPlayerRef = useRef<Record<number, boolean>>({ 1: false, 2: false });
    const needsForwardByPlayerRef = useRef<Record<number, boolean>>({ 1: false, 2: false });

    const pendingStartMugAfterCheersRef = useRef(false);

    // === Previous state tracking ===
    const prevPhaseRef = useRef<GamePhase | null>(null);
    const prevTurnPlayerIdRef = useRef<number | null>(null);
    const prevConsecutivePassesRef = useRef<number | null>(null);
    const prevCheersPlayingRef = useRef(false);

    // === Process queue - start next animation if idle ===
    const processQueue = useCallback((half: 'top' | 'bottom') => {
        const queue = half === 'top' ? topQueueRef : bottomQueueRef;
        const playingRef = half === 'top' ? topPlayingRef : bottomPlayingRef;
        const keyRef = half === 'top' ? topRequestKeyRef : bottomRequestKeyRef;

        // If already playing or queue is empty, do nothing
        if (playingRef.current || queue.current.length === 0) {
            return;
        }

        // Get next item from queue
        const next = queue.current.shift()!;
        playingRef.current = true;
        keyRef.current++;

        // Lock Pass button for 1 second
        if (passLockTimeoutRef.current) {
            clearTimeout(passLockTimeoutRef.current);
        }
        setPassButtonLocked(true);
        passLockTimeoutRef.current = setTimeout(() => {
            setPassButtonLocked(false);
            passLockTimeoutRef.current = null;
        }, 1000);

        console.log(`[MugController] Starting queued ${next.direction} on ${half}, key:`, keyRef.current, 'remaining in queue:', queue.current.length);

        setMugRequest(prev => ({
            ...prev,
            [half]: { direction: next.direction, requestKey: keyRef.current }
        }));
    }, []);

    // === Enqueue animation - add to queue and try to process ===
    const enqueueHalf = useCallback((half: 'top' | 'bottom', direction: 'forward' | 'reverse') => {
        const queue = half === 'top' ? topQueueRef : bottomQueueRef;

        console.log(`[MugController] Enqueueing ${direction} on ${half} half, queue size before:`, queue.current.length);

        queue.current.push({ direction });
        processQueue(half);
    }, [processQueue]);

    // === Called when a half finishes its animation ===
    const onHalfComplete = useCallback((half: 'top' | 'bottom') => {
        const playingRef = half === 'top' ? topPlayingRef : bottomPlayingRef;

        console.log(`[MugController] ${half} half completed`);
        playingRef.current = false;

        // Process next item in queue
        processQueue(half);
    }, [processQueue]);

    // === Event detection and handling ===
    useEffect(() => {
        if (!gameState) return;

        const currentPhase = gameState.phase;
        const currentTurnPlayerId = gameState.turnPlayerId;
        const currentConsecutivePasses = gameState.consecutivePasses;
        const currentRound = gameState.round;

        const prevPhase = prevPhaseRef.current;
        const prevTurnPlayerId = prevTurnPlayerIdRef.current;
        const prevConsecutivePasses = prevConsecutivePassesRef.current;

        // === NightStart: not in night-context -> Combat ===
        const isNewNightStart = prevPhase !== null &&
            !isNightContext(prevPhase) &&
            isCombatPhase(currentPhase) &&
            nightKeyRef.current !== currentRound;

        if (isNewNightStart) {
            console.log('[MugController] NightStart detected, round:', currentRound, 'prevPhase:', prevPhase);

            // Reset per-night state for BOTH players
            nightKeyRef.current = currentRound;
            nightFirstPlayerIdRef.current = gameState.firstPlayerIdForNight;
            reversePlayedByPlayerRef.current = { 1: false, 2: false };
            needsForwardByPlayerRef.current = { 1: false, 2: false };
            pendingStartMugAfterCheersRef.current = true;

            // Clear any pending animations from previous night
            topQueueRef.current = [];
            bottomQueueRef.current = [];

            // Trigger Cheers animation
            setCheersPlayKey(prev => prev + 1);
        }

        // === DayStart: not Preparation -> Preparation ===
        const isNewDayStart = prevPhase !== null &&
            !isPreparationPhase(prevPhase) &&
            isPreparationPhase(currentPhase);

        if (isNewDayStart) {
            console.log('[MugController] DayStart detected, prevPhase:', prevPhase);

            // Clear queues when transitioning to day
            topQueueRef.current = [];
            bottomQueueRef.current = [];

            // Trigger Cheers animation for day
            setCheersPlayKey(prev => prev + 1);
        }

        // === NightEnd: any night context phase -> RoundEnd ===
        if (prevPhase !== null && isNightContext(prevPhase) && currentPhase === GamePhase.RoundEnd) {
            console.log('[MugController] NightEnd detected');

            // Enqueue forward for any player who still needs it
            for (const playerId of [1, 2]) {
                if (needsForwardByPlayerRef.current[playerId]) {
                    const half = halfForPlayer(playerId, localPlayerId);
                    console.log(`[MugController] Enqueueing forward before day for player ${playerId} (${half})`);
                    enqueueHalf(half, 'forward');
                    needsForwardByPlayerRef.current[playerId] = false;
                }
            }
        }

        // === PassTurnEvent: turnPlayerId changed AND consecutivePasses increased by 1 ===
        if (
            prevTurnPlayerId !== null &&
            prevConsecutivePasses !== null &&
            prevTurnPlayerId !== currentTurnPlayerId &&
            currentConsecutivePasses === prevConsecutivePasses + 1 &&
            isNightContext(currentPhase)
        ) {
            const passerId = prevTurnPlayerId;
            const receiverId = currentTurnPlayerId;

            console.log('[MugController] PassTurnEvent detected, passer:', passerId, 'receiver:', receiverId);

            // Passer: enqueue reverse (once per night)
            if (!reversePlayedByPlayerRef.current[passerId]) {
                const passerHalf = halfForPlayer(passerId, localPlayerId);
                console.log(`[MugController] Enqueueing reverse for passer ${passerId} (${passerHalf})`);
                enqueueHalf(passerHalf, 'reverse');
                reversePlayedByPlayerRef.current[passerId] = true;
                needsForwardByPlayerRef.current[passerId] = true;
            } else {
                console.log(`[MugController] Skipping reverse for passer ${passerId} (already played)`);
            }

            // Receiver: enqueue forward (if pending from previous reverse)
            if (needsForwardByPlayerRef.current[receiverId]) {
                const receiverHalf = halfForPlayer(receiverId, localPlayerId);
                console.log(`[MugController] Enqueueing forward for receiver ${receiverId} (${receiverHalf})`);
                enqueueHalf(receiverHalf, 'forward');
                needsForwardByPlayerRef.current[receiverId] = false;
            } else {
                console.log(`[MugController] Skipping forward for receiver ${receiverId} (no pending)`);
            }
        }

        // Update previous state refs
        prevPhaseRef.current = currentPhase;
        prevTurnPlayerIdRef.current = currentTurnPlayerId;
        prevConsecutivePassesRef.current = currentConsecutivePasses;
    }, [gameState?.phase, gameState?.turnPlayerId, gameState?.consecutivePasses, gameState?.round, localPlayerId, enqueueHalf]);

    // === Cheers finished -> Trigger initial TakePut ===
    useEffect(() => {
        const prevCheersPlaying = prevCheersPlayingRef.current;
        prevCheersPlayingRef.current = cheersPlaying;

        // Cheers just ended (was playing, now not)
        if (prevCheersPlaying && !cheersPlaying && pendingStartMugAfterCheersRef.current) {
            pendingStartMugAfterCheersRef.current = false;

            const nightFirstPlayerId = nightFirstPlayerIdRef.current;
            if (nightFirstPlayerId === null) return;

            console.log('[MugController] Cheers ended, nightFirstPlayerId:', nightFirstPlayerId);

            // Second player is the one who waits (not going first)
            const nightSecondPlayerId = nightFirstPlayerId === 1 ? 2 : 1;

            // Enqueue reverse on SECOND player's side (they put the mug down while waiting)
            const secondPlayerHalf = halfForPlayer(nightSecondPlayerId, localPlayerId);
            console.log(`[MugController] Enqueueing initial reverse for second player ${nightSecondPlayerId} (${secondPlayerHalf})`);
            enqueueHalf(secondPlayerHalf, 'reverse');

            // Mark second player's reverse as played
            reversePlayedByPlayerRef.current[nightSecondPlayerId] = true;
            needsForwardByPlayerRef.current[nightSecondPlayerId] = true;
        }
    }, [cheersPlaying, localPlayerId, enqueueHalf]);

    // === Callbacks ===
    const onCheersDone = useMemo(() => () => {
        // Currently handled by cheersPlaying state change
    }, []);

    const onMugDone = useMemo(() => () => {
        console.log('[MugController] Mug animation done');
    }, []);

    return {
        cheersPlayKey,
        mugRequest,
        onCheersDone,
        onMugDone,
        onHalfComplete,
        passButtonLocked,
    };
};
